/**
 * Error Boundary - catches JavaScript errors in child components.
 * Shows a fallback UI instead of crashing the whole app.
 */
import { Component, ReactNode, ErrorInfo } from 'react';
import { AlertTriangle } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to console in development
    console.error('Error caught by boundary:', error, errorInfo);

    this.setState({ errorInfo });

    // In production, you might send this to an error reporting service
    // For now, just log to console
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div className="flex h-full min-h-[200px] flex-col items-center justify-center bg-background p-4 text-center">
          <div className="mb-4 text-error">
            <AlertTriangle size={48} />
          </div>
          <h2 className="mb-2 text-lg font-semibold text-text">
            Something went wrong
          </h2>
          <p className="mb-4 text-sm text-text-muted">
            An unexpected error occurred. Please try again.
          </p>

          <button
            onClick={this.handleRetry}
            className="rounded-lg bg-primary px-6 py-2 text-sm font-medium text-text-inverse transition-colors hover:bg-primary/90"
          >
            Try Again
          </button>

          {/* Show error details in development */}
          {import.meta.env.DEV && this.state.error && (
            <details className="mt-4 w-full max-w-md text-left">
              <summary className="cursor-pointer text-xs text-text-muted">
                Error Details (dev only)
              </summary>
              <pre className="mt-2 overflow-auto rounded bg-surface p-2 text-xs text-error">
                {this.state.error.toString()}
                {this.state.errorInfo?.componentStack}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Compact error boundary for individual components.
 * Shows a smaller error message inline.
 */
export class InlineErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Inline error boundary:', error, errorInfo);
    this.setState({ errorInfo });
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex items-center gap-2 rounded-lg bg-error-light p-3 text-sm text-error">
          <span>Failed to load</span>
          <button
            onClick={this.handleRetry}
            className="underline hover:no-underline"
          >
            Retry
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
